import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { searchShifts } from '../../services/shifts';
import { searchSites } from '../../services/sites';
import { searchOfficers } from '../../services/users';
import { Col, Form, Row } from 'react-bootstrap';
import CustomButton from '../shared/CustomButton';
import { FaSave } from 'react-icons/fa';
import CustomAsyncSelect from './../form_components/CustomAsyncSelect';

const ScheduleForm = ({
    schedule,
    isSaving,
    formAction
}) => {
    const {
        register,
        reset,
        handleSubmit,
        formState: {errors},
        setError,
        getValues,
        control
    } = useForm({defaultValues: schedule ? 
        {
            id: schedule.id,
            site: {
                label: schedule.site.name,
                value: schedule.site.id
            },
            shift: {
                label: schedule.shift.name,
                value: schedule.shift.id
            },
            officer: {
                label: schedule.officer.name,
                value: schedule.officer.id
            },
            start_date: schedule.start_date,
            end_date: schedule.end_date
        } : {}
    });

    useEffect(() => {
        if (schedule){
            reset({
                id: schedule.id,
                site: {
                    label: schedule.site.name,
                    value: schedule.site.id
                },
                shift: {
                    label: schedule.shift.name,
                    value: schedule.shift.id
                },
                officer: {
                    label: schedule.officer.name,
                    value: schedule.officer.id
                },
                start_date: schedule.start_date,
                end_date: schedule.end_date
            });
        } else {
            reset({
                id: '',
                site: null,
                shift: null,
                officer: null,
                start_date: '',
                end_date: ''
            });
        }
    }, [reset, schedule]);

    const resetForm = () => {
        reset({
            id: '',
            site: null,
            shift: null,
            officer: null,
            start_date: '',
            end_date: ''
        });
    }

    /**
     * search for officers using name
     * @param {*} inputValue
     * @returns 
     */
    const loadOfficers = async (inputValue) => {
        if (inputValue.length < 3){
            return [];
        }
        try {
            const response = await searchOfficers(inputValue);
            return response.data.map((officer) => ({label: officer.name, value: officer.id, ...officer}));
        } catch(error){
            return [];
        }
    }

    /**
     * search for sites 
     * @param {*} inputValue 
     * @returns 
     */
    const loadSites = async (inputValue) => {
        if (inputValue.length < 3){
            return [];
        }
        try {
            const response = await searchSites(inputValue);
            return response.data.map((site) => ({label: site.name, value: site.id, ...site}));
        } catch (error){
            return [];
        }
    }

    /**
     * search for shifts using name
     * @param {*} inputValue 
     * @returns 
     */
    const loadShifts = async (inputValue) => {
        if (inputValue.length < 3){
            return [];
        }
        try {
            const response = await searchShifts(inputValue);
            return response.data.map((shift) => ({label: shift.name, value: shift.id, ...shift}));
        } catch(error){
            return [];
        }
    }

    const onSubmit = async (data) => {
        try {
            const success = await formAction(data);
            if (success){
                resetForm();
            }
        } catch (error){
            if (error.response && error.response.status === 422){
                Object.keys(error.response.data.errors).forEach((field) => {
                    setError(field, {
                        type: 'manual',
                        message: error.response.data.errors[field][0]
                    });
                });
            }
        }
    }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>

        <Row>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='site'>Site</Form.Label>
                    <CustomAsyncSelect
                        name='site'
                        placeholder='Select site'
                        control={control}
                        loadOptions={loadSites}
                        rules={{ required: 'Select site'}}
                    />
                    {errors.site_id && (
                        <div className="invalid-feedback">{errors.site_id.message}</div>
                    )}

                </Form.Group>
            
            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='shift'>Shift</Form.Label>
                    <CustomAsyncSelect
                        name='shift'
                        placeholder='Select shift'
                        control={control}
                        loadOptions={loadShifts}
                        rules={{ required: 'Select shift'}}
                    />
                    {errors.shift_id && (
                        <div className="invalid-feedback">{errors.shift_id.message}</div>
                    )}

                </Form.Group>
            
            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='officer'>Officer</Form.Label>
                    <CustomAsyncSelect
                        name='officer'
                        control={control}
                        placeholder='Officer'
                        loadOptions={loadOfficers}
                        rules={{ required: 'Select officer'}}
                    />
                    {errors.officer_id && (
                        <div className="invalid-feedback">{errors.officer_id.message}</div>
                    )}
                </Form.Group>
            
            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='start_date'>Start Date</Form.Label>
                    <input type='date' autoComplete='off' placeholder='Start Date'
                        className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                        {...register('start_date', {
                            required: 'Select the start date of the schedule'
                        })}
                    />
                    {errors.start_date && (
                        <div className="invalid-feedback">{errors.start_date.message}</div>
                    )}

                </Form.Group>
            
            </Col>

        </Row>

        <Row>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='end_date'>End Date</Form.Label>
                    <input type='date' autoComplete='off' placeholder='End Date'
                        className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
                        {...register('end_date', {
                            required: 'Select the end date for the schedule',
                            validate: (value) => {
                                if (value <= getValues('start_date')) {
                                    return 'End date must be after start date'
                                }
                                return true;
                            }
                        })}
                    />
                    {errors.end_date && (
                        <div className="invalid-feedback">{errors.end_date.message}</div>
                    )}
                    
                </Form.Group>
            
            </Col>

        </Row>

        <CustomButton
            isSaving={isSaving}
            title={isSaving ? 'Saving' : 'Save'}
            icon={<FaSave />}
        />

    </Form>
  )
}

export default ScheduleForm